import { Check, Close, Visibility } from '@mui/icons-material';
import { Grid, IconButton, useEventCallback } from '@mui/material';
import { memo, ReactElement, useEffect, useMemo, useState } from 'react';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { CriteriRicercaNotifiche } from 'civil-status/model/notifications';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import {
  getValueStateNotification,
  STATES_NOTIFICATIONS,
} from 'civil-status/constants/states-notifications';
import { HeadCell } from 'shell/components/shared/table/types';
import { NotificationsListSide } from 'civil-status/view-models/notifications-list-side';
import { NotificationsListAnscOperator } from 'civil-status/view-models/notifications-list-ansc-operator';
import { CodeScope } from 'civil-status/constants/code-scope';
import { HttpResponse } from 'shell/models/http-response';
import { ResponsePreview } from 'civil-status/view-models/step-form';
import { AxiosResponse } from 'axios';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import ModalPrintSuccess from 'practice/components/detail/form/print-comunication/modal';
import { PmiButton } from 'shell/components/shared/button';
import ModalConfirmNotification from './modal-confirm-notification';
import { ISTAT_MILANO } from 'shell/constants/istat-milano';

interface TableActProps {
  data?: Array<NotificationsListSide | NotificationsListAnscOperator>;
  filterSelected?: CriteriRicercaNotifiche;
  getNotificationsListData: (
    data?: CriteriRicercaNotifiche,
    page?: number,
    rowsPerPage?: number
  ) => void;
  onConfirmMassively: (data?: CriteriRicercaNotifiche) => void;
  isWithConfirmMassively?: boolean;
  tableProps?: Record<string, number>;
  setTableProps: (values: Record<string, number>) => void;
  idCat?: string;
  nameCat?: string;
  headCells?:
    | HeadCell<NotificationsListSide, keyof NotificationsListSide>[]
    | HeadCell<NotificationsListAnscOperator, keyof NotificationsListAnscOperator>[];
  setTablePropsFilterTable: (page?: number, rowsPerPage?: number) => void;
  minWidth?: number;
}

const TableActComponent = ({
  data,
  filterSelected,
  getNotificationsListData,
  onConfirmMassively,
  isWithConfirmMassively = false,
  tableProps = {},
  setTableProps,
  setTablePropsFilterTable,
  headCells = [],
  minWidth,
}: TableActProps): ReactElement => {
  // stato per stampa anteprima
  const [pdfModal, setPdfModal] = useState({
    data: '',
    open: false,
    name: '',
    item: {},
  });

  // ModalPrintSuccess
  const [modalPrintSuccess, setModalPrintSuccess] = useState<{
    open: boolean;
    total?: number;
  }>({
    open: false,
  });

  // modale conferma massiva
  const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);

  const [dataList, setDataList] = useState<
    Array<NotificationsListAnscOperator | NotificationsListSide> | undefined
  >();
  const [itemToModal, setItemToModal] = useState<{
    item?: NotificationsListAnscOperator | NotificationsListSide;
    isConfirm: boolean;
  }>({
    item: undefined,
    isConfirm: false,
  });

  useEffect(() => {
    setDataList(data);
  }, [data]);

  const titleModal: string = useMemo(() => {
    if (itemToModal.isConfirm) {
      return 'ACCETTAZIONE NOTIFICA';
    }

    return 'RIFIUTO NOTIFICA';
  }, [itemToModal.item]);

  const messageModal: string = useMemo(() => {
    return `Hai selezionato l'ID evento ${
      (itemToModal.item as NotificationsListAnscOperator)?.sourceEvent || ''
    } con intestatario ${
      (itemToModal.item as NotificationsListAnscOperator)?.holders || ''
    }. Una volta ${
      itemToModal.isConfirm ? 'confermata' : 'rifiutata'
    } la notifica la scelta non potrà essere modificata. Sei sicuro di voler procedere?`;
  }, [itemToModal.item]);

  // old detail press
  // const onDetailPress = useEventCallback((item: NotificationsListAnscOperator) => {
  //   if (!(item.idAnsc && (item.sourceEvent || item.idNotifica) && item.state)) {
  //     return;
  //   }
  //   if (idCat && nameCat) {
  //     navigate({
  //       pathname: generatePath(
  //         '/stato-civile/notifiche/ansc/ricerca/operatore/:valueCat/:idCat/visualizza/:id/:idNotification/:stateNotification',
  //         {
  //           id: item.idAnsc,
  //           valueCat: nameCat,
  //           idCat: idCat,
  //           idNotification: item.idNotifica || item.sourceEvent || '',
  //           stateNotification: item.state,
  //         }
  //       ),
  //     });

  //     return;
  //   }

  //   navigate({
  //     pathname: generatePath(
  //       '/stato-civile/notifiche/ansc/ricerca/operatore/visualizza/:id/:idNotification/:stateNotification',
  //       {
  //         id: item.idAnsc,
  //         idNotification: item.idNotifica || item.sourceEvent || '',
  //         stateNotification: item.state,
  //       }
  //     ),
  //   });
  // });

  const onPreview = useHttpClient('', {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  const onDetailPress = useEventCallback((item: NotificationsListAnscOperator) => {
    if (!item.idAnsc) {
      return;
    }

    (
      onPreview({
        url: `${__ACT_API_URL__}/evento/anteprima/idAnsc/${item.idAnsc}`,
      }) as unknown as ResponsePromise<HttpResponse<ResponsePreview>, HttpResponse<ResponsePreview>>
    ).then((response) => {
      toast.success('Operazione eseguita con successo');

      const responseCasted = response as AxiosResponse<HttpResponse<ResponsePreview>>;

      const pdf = responseCasted.data.payload.eventoOutput?.contenuto;
      if (pdf) {
        setPdfModal({
          data: pdf,
          name: 'Anteprima atto',
          open: true,
          item,
        });
      }
    });
  });

  const onConfirm = useHttpClient(`${__ACT_API_URL__}/notifiche/conferma`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onApproval = useHttpClient(`${__ACT_API_URL__}/notifiche/approvazione`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onApprovalNotifications = useHttpClient(
    `${__ACT_API_URL__}/notifiche-ansc/approvazione-notifiche`,
    {
      options: {
        method: 'POST',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onYesConfirm = useEventCallback((item?: NotificationsListAnscOperator) => {
    if (!item?.sourceEvent) {
      return;
    }

    const dataConfirm = {
      idNotifica: item?.idNotifica || undefined,
    };

    (
      onConfirm({
        data: dataConfirm,
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      setItemToModal({ item: undefined, isConfirm: false });

      // ricaricare la tabella
      getNotificationsListData(filterSelected || {}, tableProps.page, tableProps.rowsPerPage);
    });

    if (item.ambito === CodeScope.ARCHIVIO || item.ambito === CodeScope.PEC) {
      onApproval({
        data: item.currentItem,
      }) as ResponsePromise<void>;
    }

    if (item?.idGenereNotifiche === '2') {
      (
        onApprovalNotifications({
          data: {
            canaleComunicazione: 'NOTIFEVENT', // fixed
            idEvento: item.currentItem?.idEventoLocale || undefined,
            idUsecase: item.currentItem?.idusecase || undefined,
            idAnsc: item.currentItem?.idAnsc || undefined,
            nomeComuneformazione: item.municipalityFormation || undefined,
          },
        }) as ResponsePromise<void>
      ).then(() => {
        // ricaricare la tabella
        getNotificationsListData(filterSelected || {}, tableProps.page, tableProps.rowsPerPage);
      });

      return;
    }
  });

  const onRejection = useHttpClient(`${__ACT_API_URL__}/notifiche/rifiuta`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onYesRejection = useEventCallback((item?: NotificationsListAnscOperator) => {
    if (!item?.idNotifica) {
      return;
    }

    const dataConfirm = {
      idNotifica: item?.idNotifica || undefined,
    };

    (
      onRejection({
        data: dataConfirm,
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      setItemToModal({ item: undefined, isConfirm: false });
      // ricaricare la tabella
      getNotificationsListData(filterSelected || {}, tableProps.page, tableProps.rowsPerPage);
    });
  });

  const body: Array<NotificationsListSide | NotificationsListAnscOperator> = useMemo(() => {
    return (dataList || []).map((element) => {
      const item = element as NotificationsListAnscOperator;
      const disabled =
        item.state === STATES_NOTIFICATIONS.ID_STATE_APPROVATO ||
        item.state === STATES_NOTIFICATIONS.ID_STATE_RIFIUTATA ||
        item.state === STATES_NOTIFICATIONS.ID_STATE_STATOINFORMATIVO;

      return {
        ...item,
        state: getValueStateNotification(item.state),
        action: (
          <div className="pmi-d-flex">
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setItemToModal({ item, isConfirm: true });
              }}
              aria-label="Conferma notifica"
              aria-haspopup="true"
              disabled={disabled}
              style={{
                padding: '4px',
              }}
            >
              <Check color={disabled ? 'secondary' : 'success'} fontSize="small" />
            </IconButton>
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setItemToModal({ item, isConfirm: false });
              }}
              aria-label="Rifiuta notifica"
              aria-haspopup="true"
              disabled={disabled}
              style={{
                padding: '4px',
              }}
            >
              <Close color={disabled ? 'secondary' : 'error'} fontSize="small" />
            </IconButton>
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onDetailPress(item);
              }}
              aria-label="Visualizza elemento"
              aria-haspopup="true"
              style={{
                padding: '4px',
              }}
            >
              <Visibility />
            </IconButton>
          </div>
        ),
      };
    });
  }, [dataList]);

  const onChangePage = useEventCallback((page: number) => {
    getNotificationsListData(filterSelected || {}, page, tableProps.rowsPerPage);

    setTablePropsFilterTable(page, tableProps.rowsPerPage);

    setTableProps({
      page,
      rowsPerPage: tableProps.rowsPerPage,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    getNotificationsListData(filterSelected || {}, 0, rowsPerPage);

    setTablePropsFilterTable(0, rowsPerPage);

    setTableProps({
      page: 0,
      rowsPerPage,
    });
  });

  const onYesModalConfirmNotification = useEventCallback(() => {
    onConfirmMassively({
      criteriRicercaNotifiche: {
        ...(filterSelected?.criteriRicercaNotifiche || {}),
        confermaMassiva: true,
      },
    });

    setOpenModalConfirm(false);
  });

  const disabledAllConfirm: boolean = useMemo(() => {
    return !(
      Boolean(filterSelected?.criteriRicercaNotifiche?.idFormula) ||
      filterSelected?.criteriRicercaNotifiche?.codAzioneCanale === 'APPROVA_ESTERO' ||
      (filterSelected?.criteriRicercaNotifiche?.codAzioneCanale === 'APPROVA_PV' &&
        filterSelected?.criteriRicercaNotifiche?.comuneFormazione === ISTAT_MILANO) ||
      (filterSelected?.criteriRicercaNotifiche?.codAzioneCanale === 'PEC' &&
        filterSelected?.criteriRicercaNotifiche?.comuneFormazione === ISTAT_MILANO) ||
      (filterSelected?.criteriRicercaNotifiche?.codAzioneCanale === 'MANUALE' &&
        filterSelected?.criteriRicercaNotifiche?.comuneFormazione === ISTAT_MILANO)
    );
  }, [filterSelected]);

  return (
    <Grid container spacing={2}>
      {isWithConfirmMassively ? (
        <Grid item xs={12} display="flex" justifyContent="end">
          <PmiButton
            color="cdmBrandThird"
            variant="outlined"
            onClick={() => setOpenModalConfirm(true)}
            disabled={disabledAllConfirm}
          >
            Conferma tutte le notifiche della pagina
          </PmiButton>
        </Grid>
      ) : undefined}
      <Grid item xs={12}>
        <Table
          ariaLabel="Lista notifiche"
          page={tableProps.page || 0}
          rowsPerPage={tableProps.rowsPerPage || 10}
          body={body}
          count={(body && body[0]?.totalCount) || 0}
          head={
            headCells as HeadCell<
              NotificationsListSide | NotificationsListAnscOperator,
              keyof (NotificationsListSide | NotificationsListAnscOperator)
            >[]
          }
          minWidth={minWidth}
          onSort={noop}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          orders={{}}
          isNotLayoutFixed
          noSort
          hideButtons
          isServer
        />
      </Grid>
      {Boolean(itemToModal.item) ? (
        <DeleteAlertModal
          title={titleModal}
          message={messageModal}
          open={Boolean(itemToModal.item)}
          onClose={() => setItemToModal({ item: undefined, isConfirm: false })}
          onYes={() => {
            if (itemToModal.isConfirm) {
              onYesConfirm(itemToModal.item as NotificationsListAnscOperator);
            } else {
              onYesRejection(itemToModal.item as NotificationsListAnscOperator);
            }
          }}
          onNo={() => setItemToModal({ item: undefined, isConfirm: false })}
        />
      ) : undefined}
      {pdfModal.open ? (
        <ModalViewerPDF
          open={pdfModal.open}
          data={pdfModal.data}
          name={pdfModal.name}
          title={pdfModal.name || 'Anteprima atto'}
          onClose={() => {
            setModalPrintSuccess((oldState) => ({
              ...oldState,
              open: true,
            }));
            setPdfModal({ data: '', open: false, name: '', item: {} });
          }}
        />
      ) : undefined}
      {modalPrintSuccess.open ? (
        <ModalPrintSuccess
          open={modalPrintSuccess.open}
          onYes={() => setModalPrintSuccess({ open: false })}
          onClose={() => onDetailPress(pdfModal.item)}
        />
      ) : undefined}
      {openModalConfirm ? (
        <ModalConfirmNotification
          open={openModalConfirm}
          onClose={setOpenModalConfirm}
          numberNotification={tableProps.rowsPerPage || 10}
          onYes={onYesModalConfirmNotification}
        />
      ) : undefined}
    </Grid>
  );
};

TableActComponent.displayName = 'Notifications Research - Table Notifications component';

const TableNotifications = memo(TableActComponent);

export default TableNotifications;
